import Input from './Input.jsx'
import NiTblAttrSelect from './NiTblAttrSelect.jsx'
import PredicateSelect from './PredicateSelect.jsx'
import AddFilterBtn from './AddFilterBtn.jsx'
import RemoveFilterBtn from './RemoveFilterBtn.jsx'
import React from 'react'

/**
 * Polymorphic/Multi-purpose Filter component
 * We use this for both selecting a filter and rendering applied/chosen filters.
 * Each ComboFilter has a minimum of 2 parts with an optional predicate as well.
 * Column name, predicate (optional), selected value.
 */
const ComboFilter = ({
  addToAppliedFilters,
  data,
  disabled,
  filterChanged,
  filterRemoved,
  filtersList,
  type
}) => {
  // State managemnt
  const [selectedColumn, setSelectedColumn] = React.useState(data ? data.selectedColumn : '')
  const [selectedPredicate, setSelectedPredicate] = React.useState(data ? data.selectedPredicate : '')
  const [selectedValues, setSelectedValues] = React.useState(data ? data.selectedValues : {})
  const [isAddable, setIsAddable] = React.useState(false)
  const unusedFiltersList = filtersList.map(({ display_name_override }) => display_name_override)

  // Adjust various state on change of either of the three
  // parts of this component (column, predicate(optional), and value.
  React.useEffect(() => {
    if (filterChanged) {
      filterChanged({ selectedPredicate, selectedColumn, selectedValues })
    }

    if (selectedColumn?.selected_filter?.predicates) {
      if (!selectedPredicate) {
        return setIsAddable(false)
      }
    }
    if (selectedPredicate.toLowerCase() === 'is between') {
      return setIsAddable(selectedValues.selectedInput && selectedValues.selectedInputOther)
    }
    return setIsAddable(selectedValues.selectedInput)
  }, [selectedPredicate, selectedColumn, selectedValues, filterChanged])

  // Adds new filter to list of applied filters.
  const addAppliedFilter = () => {
    addToAppliedFilters({ selectedColumn, selectedPredicate, selectedValues })
    setSelectedColumn('')
    setSelectedPredicate('')
    setSelectedValues({})
  }

  // When the column type changes we need to reset the value
  // and predicate as each column type has it's own types and options.
  const handleChange = (current) => (event) => {
    setSelectedColumn(
      filtersList.find((c) => c.display_name_override === event.target.value)
    )
    setSelectedPredicate('')
    setSelectedValues({})
  }

  const handlePredicateChange = (e, _, uid) => {
    setSelectedPredicate(e.target.value)
    setSelectedValues({ ...selectedValues, id: uid })
  }

  const multiInputChange = (e, num, uid) => {
    if (e.target.type === 'checkbox') {
      if (e.target.checked) {
        setSelectedValues((prevState) => {
          if (prevState.selectedInput) {
            return { selectedInput: [...prevState.selectedInput, e.target.value], id: uid }
          } else {
            return { selectedInput: [e.target.value], id: uid }
          }
        })
      } else {
        setSelectedValues((prevState) => { return { selectedInput: [...prevState.selectedInput.filter(n => n !== e.target.value)], id: uid } })
      }
    } else if (e.target.type === 'multiselect') {
      setSelectedValues({ selectedInput: e.target.value, id: uid })
    } else if (e.target.type === 'radio') {
      setSelectedValues({ selectedInput: e.target.value, id: uid })
    } else if (e.target.type === 'range') {
      setSelectedValues({ selectedInput: num, id: uid })
    } else {
      setSelectedValues({
        ...selectedValues,
        [e.target.name]: e.target.value,
        id: uid
      })
    }
  }

  const predicateSelect = selectedColumn?.selected_filter?.predicates
    ? (
      <PredicateSelect
        value={selectedPredicate}
        onChange={handlePredicateChange}
        list={selectedColumn?.selected_filter?.predicates}
        uid={data?.id}
      />
      )
    : null

  let selectedInput = null
  let selectedInputOther = null

  if (selectedColumn) {
    selectedInput = (
      <Input
        type={selectedColumn?.selected_filter.type}
        handleInputChange={multiInputChange}
        value={selectedValues?.selectedInput}
        name='selectedInput'
        nwTblAttrType={selectedColumn?.ni_table_attribute_type}
        attributes={selectedColumn?.selected_filter.attributes}
        uid={data?.id}
      />
    )

    if (selectedPredicate && selectedPredicate.toLowerCase() === 'is between') {
      selectedInputOther = (
        <Input
          type={selectedColumn?.selected_filter.type}
          handleInputChange={multiInputChange}
          value={selectedValues?.selectedInputOther}
          name='selectedInputOther'
          nwTblAttrType={selectedColumn?.ni_table_attribute_type}
          attributes={selectedColumn?.selected_filter.attributes}
          uid={data?.id}
        />
      )
    }
  }

  // Each filter is composed of 2-3 selections
  // 1. The NiTableAttribute
  // 2. The Predicate (Optional)
  // 3. The value to filter by.
  return (
    <div className='d-flex align-items-center'>
      <NiTblAttrSelect
        value={selectedColumn?.display_name_override}
        onChange={handleChange(selectedColumn?.display_name_override)}
        disabled={disabled || unusedFiltersList.length === 0}
        list={unusedFiltersList}
      />
      {predicateSelect}
      {selectedInput}
      {selectedInputOther}
      {!data && <AddFilterBtn disabled={!isAddable} onClick={addAppliedFilter} />}
      {filterRemoved && <RemoveFilterBtn onClick={filterRemoved} />}
    </div>
  )
}

export default ComboFilter
